// src/pages/channel-order/BulkUploadModal.jsx
// @ts-nocheck
import React, { useState, useRef } from "react"
import axios from "axios"
import { useToast } from "../../components/toast/ToastProvider"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"

const lottieSrc = "/animations/loader.lottie"

const Icon = {
  X: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  Upload: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  File: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeWidth="2"/>
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Check: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Download: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  AlertCircle: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="12" cy="12" r="10" strokeWidth="2"/>
      <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="12" cy="16" r="0.5" fill="currentColor" strokeWidth="1"/>
    </svg>
  ),
}

export default function BulkUploadModal({ open, onClose, onSuccess }) {
  const { showToast } = useToast()
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [merchantId, setMerchantId] = useState("")
  const [channelName, setChannelName] = useState("SHOPIFY")
  const [dragActive, setDragActive] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // Get merchant ID from localStorage or token
  React.useEffect(() => {
    if (open) {
      // First try localStorage
      const storedMerchantId = localStorage.getItem("merchantId") || localStorage.getItem("parentClientId")
      if (storedMerchantId) {
        setMerchantId(storedMerchantId)
        return
      }
      
      // Fallback to token
      try {
        const token = localStorage.getItem("authToken") || ""
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]))
          setMerchantId(payload.merchantId || payload.clientId || "")
        }
      } catch (err) {
        console.error("Failed to parse token", err)
      }
    }
  }, [open])

  const handleFileSelect = (file) => {
    if (file && file.type === "text/csv") {
      setSelectedFile(file)
      setUploadProgress(0)
    } else {
      showToast("Please select a valid CSV file", "error")
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0])
    }
  }

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast("Please select a file to upload", "error")
      return
    }

    if (!merchantId) {
      showToast("Merchant ID is required", "error")
      return
    }

    setUploading(true)
    setUploadProgress(0)

    try {
      const token = localStorage.getItem("authToken") || ""
      if (!token) {
        throw new Error("Not authenticated")
      }

      const POCAS_BASE_URL = process.env.REACT_APP_POCAS_BASE_URL || "http://localhost:8082"
      const formData = new FormData()
      formData.append("merchant_id", merchantId)
      formData.append("channel_name", channelName)
      formData.append("file", selectedFile)

      const response = await axios.post(
        `${POCAS_BASE_URL}/v1/pocas/channel-orders/upload-for-fullfillment`,
        formData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            )
            setUploadProgress(percentCompleted)
          },
        }
      )

      // Show success state
      setUploading(false)
      setUploadSuccess(true)
      setUploadProgress(100)
      
      showToast("Your file is accepted for order fulfillment", "success")
      
      // Reset and close after showing success message
      setTimeout(() => {
        setSelectedFile(null)
        setUploadProgress(0)
        setUploadSuccess(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
        if (onSuccess) {
          onSuccess()
        }
        onClose()
      }, 2500)
    } catch (error) {
      console.error("Upload error:", error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload file. Please try again."
      showToast(errorMessage, "error")
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadSample = () => {
    const link = document.createElement("a")
    link.href = "/ChannelOrderIdProcesssing.csv"
    link.download = "ChannelOrderIdProcesssing.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    showToast("Sample file download started", "success")
  }

  const handleClose = () => {
    if (!uploading && !uploadSuccess) {
      setSelectedFile(null)
      setUploadProgress(0)
      setUploadSuccess(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      onClose()
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-2 md:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="bg-white w-full max-w-2xl rounded-none sm:rounded-xl md:rounded-2xl shadow-2xl overflow-hidden relative mt-0 sm:my-auto mb-0 sm:mb-auto max-h-screen sm:max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ marginTop: '0', marginBottom: '0' }}
      >
        {/* Header - Sticky at top */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5 flex-shrink-0 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-lg sm:rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Icon.Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg md:text-xl font-bold text-white truncate">Bulk Order Upload</h2>
                <p className="text-[10px] sm:text-xs md:text-sm text-indigo-100 truncate">Upload CSV file to process Shopify orders</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={uploading || uploadSuccess}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/20 hover:bg-white/30 active:bg-white/40 flex items-center justify-center transition-colors disabled:opacity-50 flex-shrink-0 touch-manipulation"
              aria-label="Close"
            >
              <Icon.X className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6 overflow-y-auto flex-1 min-h-0">
          {/* Merchant ID and Channel Name - Fixed */}
          {!uploading && !uploadSuccess && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
              <div>
                <label className="block text-[11px] sm:text-xs md:text-sm font-semibold text-slate-700 mb-1 sm:mb-1.5 md:mb-2">
                  Merchant ID <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={merchantId}
                  readOnly
                  disabled={uploading}
                  className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg sm:rounded-xl border border-slate-300 bg-slate-50 text-slate-700 cursor-not-allowed outline-none transition-all disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-[11px] sm:text-xs md:text-sm font-semibold text-slate-700 mb-1 sm:mb-1.5 md:mb-2">
                  Channel Name <span className="text-rose-500">*</span>
                </label>
                <select
                  value={channelName}
                  readOnly
                  disabled={uploading}
                  className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg sm:rounded-xl border border-slate-300 bg-slate-50 text-slate-700 cursor-not-allowed outline-none transition-all disabled:opacity-50 appearance-none"
                >
                  <option value="SHOPIFY">SHOPIFY</option>
                  <option value="AMAZON">AMAZON</option>
                </select>
              </div>
            </div>
          )}

          {/* File Upload Area */}
          {!uploading && !uploadSuccess && (
            <div>
              <label className="block text-[11px] sm:text-xs md:text-sm font-semibold text-slate-700 mb-1 sm:mb-1.5 md:mb-2">
                CSV File <span className="text-rose-500">*</span>
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 lg:p-8 text-center transition-all ${
                  dragActive
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-300 hover:border-indigo-400 hover:bg-slate-50 active:bg-indigo-50"
                } cursor-pointer touch-manipulation`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Icon.File className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-emerald-600" />
                    </div>
                    <div className="px-1">
                      <p className="text-[11px] sm:text-xs md:text-sm font-semibold text-slate-900 break-words">{selectedFile.name}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFile(null)
                        if (fileInputRef.current) {
                          fileInputRef.current.value = ""
                        }
                      }}
                      className="text-[10px] sm:text-xs text-rose-600 hover:text-rose-700 active:text-rose-800 font-medium touch-manipulation"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center">
                      <Icon.Upload className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-[11px] sm:text-xs md:text-sm font-semibold text-slate-700 px-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">CSV file only (Max 10MB)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-3 sm:space-y-4 md:space-y-6 py-3 sm:py-4 md:py-6">
              <div className="flex flex-col items-center justify-center">
                <div className="mb-2 sm:mb-3 md:mb-4 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20">
                  <DotLottieReact src={lottieSrc} loop autoplay style={{ width: '100%', height: '100%' }} />
                </div>
                <div className="text-center space-y-1 sm:space-y-1.5 md:space-y-2 px-2">
                  <p className="text-xs sm:text-sm md:text-base font-semibold text-slate-900">Uploading your file...</p>
                  <p className="text-[11px] sm:text-xs md:text-sm text-slate-600">Please wait while we process your order file</p>
                </div>
              </div>
              <div className="space-y-1.5 sm:space-y-2 px-2">
                <div className="flex items-center justify-between text-[11px] sm:text-xs md:text-sm">
                  <span className="text-slate-700 font-medium">Progress</span>
                  <span className="text-indigo-600 font-semibold">{uploadProgress}%</span>
                </div>
                <div className="h-2 sm:h-2.5 md:h-3 w-full bg-slate-200 rounded-full overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-600 to-violet-600 transition-all duration-300 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {uploadSuccess && (
            <div className="space-y-3 sm:space-y-4 md:space-y-6 py-3 sm:py-4 md:py-6">
              <div className="flex flex-col items-center justify-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-2 sm:mb-3 md:mb-4 ring-4 ring-emerald-50">
                  <Icon.Check className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-emerald-600" />
                </div>
                <div className="text-center space-y-1 sm:space-y-1.5 md:space-y-2 px-2">
                  <p className="text-sm sm:text-base md:text-lg font-bold text-slate-900">Upload Successful!</p>
                  <p className="text-xs sm:text-sm md:text-base text-emerald-700 font-medium">
                    Your file is accepted for order fulfillment
                  </p>
                  <p className="text-[11px] sm:text-xs md:text-sm text-slate-600 mt-1 sm:mt-2">
                    The file will be processed shortly. You can close this window.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          {!uploading && !uploadSuccess && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-2.5 sm:p-3 md:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <Icon.AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-[11px] sm:text-xs md:text-sm text-blue-800">
                  <p className="font-semibold mb-0.5 sm:mb-1">File Format Requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5 sm:space-y-1 text-[10px] sm:text-[11px] md:text-xs leading-relaxed">
                    <li>CSV file with column: <code className="bg-blue-100 px-1 rounded text-[10px] sm:text-[11px]">channel_order_id</code></li>
                    <li>Maximum file size: 10MB</li>
                    <li>Ensure all order IDs are valid and exist in your system</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {!uploading && !uploadSuccess && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200 flex-shrink-0">
              <button
                onClick={handleDownloadSample}
                disabled={uploading}
                className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-xs md:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg sm:rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                <Icon.Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="whitespace-nowrap">Download Sample</span>
              </button>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={handleClose}
                  disabled={uploading}
                  className="flex-1 sm:flex-none px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 text-[11px] sm:text-xs md:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg sm:rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || !merchantId || uploading}
                  className="flex-1 sm:flex-none px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 text-[11px] sm:text-xs md:text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 rounded-lg sm:rounded-xl hover:from-indigo-500 hover:to-violet-500 active:from-indigo-700 active:to-violet-700 shadow-lg shadow-indigo-500/30 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none touch-manipulation"
                >
                  <span className="inline-flex items-center gap-1.5 sm:gap-2 justify-center">
                    <Icon.Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="whitespace-nowrap">Upload & Process</span>
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

